import React, { useState, useEffect, useRef } from "react";
import ReactDOM from 'react-dom';
import { useDispatch } from "react-redux";
import { FiX } from 'react-icons/fi';
import SelectSearch from 'react-select-search';
import Fuse from 'fuse.js';
import axios from 'axios';
import { API_URLS, BASE_URL, getTeamImageUrl } from "../constants/config";
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import { useChampionships } from '../utilities/championships';
import { champFields, extractEntity, extractList, normalizeSeasonTeam } from '../utilities/tournamentsApi';
import { extractAdminStaff, staffDisplayName } from '../utilities/matchStaffApi';
import { authorizedPost } from '../utilities/stadiumsApi';
import {
  extractLeagueMatchDefaults,
  mergeLeagueInfoCache,
  formatKickoff,
  estimatePlayoffExpectedSlots,
  formatPlayoffNetPositionLabel,
  isPlayoffStage,
  playoffNetPositionPairingHint,
} from '../utilities/matchesApi';
import { kickoffFieldsForApi, resolveMatchTimezone } from '../utilities/matchKickoff';
import { useI18n } from '../utilities/i18n';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import '../styles/createMatchModal.css';

const LAST_MATCH_KEY = 'lastCreatedMatchData';

const readLastCreatedMatch = () => {
  try {
    return JSON.parse(localStorage.getItem(LAST_MATCH_KEY) || 'null');
  } catch (error) {
    return null;
  }
};

const patchLastCreatedMatch = (patch) => {
  const saved = readLastCreatedMatch();
  if (!saved || typeof saved !== 'object') return;
  localStorage.setItem(LAST_MATCH_KEY, JSON.stringify({ ...saved, ...patch }));
};

function CreateMatchModal({
  isOpen,
  onClose,
  onSuccess,
  preselectedLeague,
  preselectedSeason,
  preselectedStage,
  preselectedCountryId,
  preselectedCountryName,
}) {
  const dispatch = useDispatch();
  const { champId, selected: championship } = useChampionships();
  const { t, lang } = useI18n();
  const contextLocked = Boolean(preselectedLeague?.value || preselectedLeague?.id);
  
  // State для данных
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [stages, setStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState(null);
  const [netPosition, setNetPosition] = useState('');

  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [homeTeam, setHomeTeam] = useState(null);
  const [guestTeam, setGuestTeam] = useState(null);
  const [isHomeTeamModalOpen, setIsHomeTeamModalOpen] = useState(false);
  const [isGuestTeamModalOpen, setIsGuestTeamModalOpen] = useState(false);
  
  // Фильтрованные списки команд
  const homeTeamOptions = teams.filter(team => team.value !== guestTeam);
  const guestTeamOptions = teams.filter(team => team.value !== homeTeam);
  
  const [stadiums, setStadiums] = useState([]);
  const [selectedStadium, setSelectedStadium] = useState(null);
  const [pitches, setPitches] = useState([]);
  const [selectedPitch, setSelectedPitch] = useState(null);
  
  const [referees, setReferees] = useState([]);
  const [selectedReferee, setSelectedReferee] = useState(null);
  
  const [matchDate, setMatchDate] = useState('');
  const [matchTime, setMatchTime] = useState('');
  const [halfTime, setHalfTime] = useState('30');
  const [extraHalfTime, setExtraHalfTime] = useState('15');
  
  const [extraTimeAvailable, setExtraTimeAvailable] = useState(false);
  const [penaltiesAvailable, setPenaltiesAvailable] = useState(false);
  const [timelineEnabled, setTimelineEnabled] = useState(true);
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  
  // Проверка дубликатов
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const restoredPlaceRef = useRef(false);
  const restoredTournamentRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      loadCountries();
      loadStadiums();
      loadReferees();
      return undefined;
    }
    restoredPlaceRef.current = false;
    restoredTournamentRef.current = false;
    return undefined;
  }, [isOpen, champId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const { body } = document;
    const prevOverflow = body.style.overflow;
    const prevOverflowX = body.style.overflowX;
    body.style.overflow = 'hidden';
    body.style.overflowX = 'hidden';
    return () => {
      body.style.overflow = prevOverflow;
      body.style.overflowX = prevOverflowX;
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) loadPitches(selectedStadium);
  }, [selectedStadium, isOpen]);

  // Восстанавливаем стадион, судью, дату и время тайма один раз на открытие
  useEffect(() => {
    if (!isOpen || restoredPlaceRef.current || preselectedLeague || preselectedSeason || preselectedStage) return;
    const saved = readLastCreatedMatch();
    const stadiumsReady = !saved?.stadium || stadiums.length > 0;
    const refereesReady = !saved?.referee || referees.length > 0;
    if (!stadiumsReady || !refereesReady) return;
    restoredPlaceRef.current = true;
    if (!saved) return;
    if (saved.stadium) setSelectedStadium(saved.stadium);
    if (saved.pitch) setSelectedPitch(saved.pitch);
    if (saved.referee) setSelectedReferee(saved.referee);
    if (saved.date) setMatchDate(saved.date);
    if (saved.halfTime) setHalfTime(saved.halfTime);
  }, [isOpen, stadiums, referees, preselectedLeague, preselectedSeason, preselectedStage]);

  const formatSeasonsList = (list) => extractList(list).map((item, index) => ({
    value: item.season_id || item.id,
    name: item.season_name || item.name,
    key: index,
    ...item,
  }));

  const ensureOption = (list, option) => {
    if (!option?.value && option?.value !== 0) return list;
    const id = Number(option.value);
    if (list.some((item) => Number(item.value) === id)) return list;
    return [option, ...list];
  };

  // Со страницы тура/стадии: страна → лига → сезон → стадия + кэш лиги
  useEffect(() => {
    if (!isOpen || !contextLocked || !preselectedLeague) return undefined;
    if (restoredTournamentRef.current) return undefined;

    let cancelled = false;

    const restoreFromContext = async () => {
      const leagueId = Number(preselectedLeague.value || preselectedLeague.id);
      if (!Number.isFinite(leagueId) || leagueId <= 0) return;

      const detailedLeague = await fetchLeagueDetails(leagueId, preselectedLeague);
      if (cancelled) return;

      const leagueReady = detailedLeague || {
        ...preselectedLeague,
        value: leagueId,
        name: preselectedLeague.name || '',
      };

      const countryId = Number(
        preselectedCountryId
        || leagueReady.country_id
        || preselectedLeague.country_id
        || preselectedLeague.country?.id
      );
      const countryName = preselectedCountryName
        || leagueReady.country_name
        || preselectedLeague.country_name
        || championship?.city
        || championship?.city_ru
        || String(countryId || '');
      const countryFromList = Number.isFinite(countryId) && countryId > 0
        ? countries.find((item) => Number(item.value) === countryId)
        : null;
      const countryToSet = countryFromList || (
        Number.isFinite(countryId) && countryId > 0
          ? { value: countryId, name: countryName || String(countryId), id: countryId }
          : null
      );

      if (countryToSet) {
        setSelectedCountry(countryToSet);
        setCountries((prev) => ensureOption(prev, countryToSet));
        try {
          const nextLeagues = await loadLeaguesForCountry(countryToSet.value);
          if (cancelled) return;
          setLeagues(ensureOption(nextLeagues || [], {
            ...leagueReady,
            value: leagueId,
            name: leagueReady.name || String(leagueId),
          }));
        } catch (_err) {
          setLeagues([{ ...leagueReady, value: leagueId, name: leagueReady.name || String(leagueId) }]);
        }
      } else {
        setLeagues([{ ...leagueReady, value: leagueId, name: leagueReady.name || String(leagueId) }]);
      }

      setSelectedLeague(leagueReady);
      applyLeagueMatchDefaults(leagueReady, { preferCacheStadium: true });

      let formattedSeasons = formatSeasonsList(leagueReady.seasons);
      const seasonId = Number(preselectedSeason?.value || preselectedSeason?.id);
      const seasonToSet = Number.isFinite(seasonId) && seasonId > 0
        ? (formattedSeasons.find((item) => Number(item.value) === seasonId) || {
          ...preselectedSeason,
          value: seasonId,
          name: preselectedSeason?.name || preselectedSeason?.season_name || String(seasonId),
        })
        : null;
      if (seasonToSet) {
        formattedSeasons = ensureOption(formattedSeasons, seasonToSet);
        setSeasons(formattedSeasons);
        setSelectedSeason(seasonToSet);
        loadTeamsForSeason(leagueId, seasonToSet.value).catch((error) => {
          console.error('Error loading teams:', error);
        });
      } else {
        setSeasons(formattedSeasons);
        if (!cancelled) restoredTournamentRef.current = true;
        return;
      }

      const stageId = Number(preselectedStage?.id || preselectedStage?.value);
      try {
        const loadedStages = await loadStagesForSeason(leagueId, seasonToSet.value);
        if (cancelled) return;
        let nextStages = loadedStages || [];
        if (Number.isFinite(stageId) && stageId > 0) {
          const stageOption = nextStages.find((item) => Number(item.value) === stageId) || {
            ...preselectedStage,
            value: stageId,
            id: stageId,
            name: preselectedStage?.name
              || preselectedStage?.short_name
              || preselectedStage?.stage_name
              || String(stageId),
          };
          nextStages = ensureOption(nextStages, stageOption);
          setStages(nextStages);
          setSelectedStage(stageId);
        } else {
          setStages(nextStages);
        }
      } catch (error) {
        console.error('Error loading stages:', error);
        if (Number.isFinite(stageId) && stageId > 0) {
          setStages([{
            ...preselectedStage,
            value: stageId,
            id: stageId,
            name: preselectedStage?.name || preselectedStage?.short_name || String(stageId),
          }]);
          setSelectedStage(stageId);
        }
      }

      if (!cancelled) restoredTournamentRef.current = true;
    };

    restoreFromContext();
    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    contextLocked,
    preselectedLeague,
    preselectedSeason,
    preselectedStage,
    preselectedCountryId,
    preselectedCountryName,
    countries,
    championship,
  ]);

  // Синхронизировать страну со списком, когда страны подгрузились после преселекта
  useEffect(() => {
    if (!isOpen || !contextLocked || !countries.length) return;
    const countryId = Number(
      preselectedCountryId
      || preselectedLeague?.country_id
      || selectedLeague?.country_id
      || selectedCountry?.value
    );
    if (!Number.isFinite(countryId) || countryId <= 0) return;
    const countryToSet = countries.find((item) => Number(item.value) === countryId);
    if (!countryToSet) return;
    if (!selectedCountry || Number(selectedCountry.value) !== Number(countryToSet.value)) {
      setSelectedCountry(countryToSet);
    }
  }, [
    isOpen,
    contextLocked,
    countries,
    selectedCountry,
    selectedLeague,
    preselectedCountryId,
    preselectedLeague,
  ]);

  // Вкладка «Игры»: восстановление из localStorage
  useEffect(() => {
    if (!isOpen || contextLocked) return undefined;
    if (countries.length === 0) return undefined;
    if (restoredTournamentRef.current) return undefined;

    let cancelled = false;
    const saved = readLastCreatedMatch();

    const restore = async () => {
      let countryToSet = null;
      if (saved?.country?.value) {
        countryToSet = countries.find((item) => Number(item.value) === Number(saved.country.value));
      }
      if (!countryToSet) {
        restoredTournamentRef.current = true;
        return;
      }
      if (cancelled) return;
      restoredTournamentRef.current = true;

      setSelectedCountry(countryToSet);
      const nextLeagues = await loadLeaguesForCountry(countryToSet.value);
      if (cancelled) return;

      const leagueToSet = saved?.league?.value
        ? nextLeagues.find((item) => item.value === saved.league.value)
        : null;
      if (!leagueToSet) return;
      const detailedLeague = await fetchLeagueDetails(leagueToSet.value, leagueToSet);
      if (cancelled) return;
      const leagueReady = detailedLeague || leagueToSet;
      setSelectedLeague(leagueReady);
      applyLeagueMatchDefaults(leagueReady, { preferCacheStadium: !saved?.stadium });

      const formattedSeasons = formatSeasonsList(leagueReady.seasons);
      if (cancelled) return;
      setSeasons(formattedSeasons);

      const seasonId = Number(saved?.season?.value);
      const seasonToSet = formattedSeasons.find((item) => Number(item.value) === seasonId);
      if (!seasonToSet) return;
      setSelectedSeason(seasonToSet);
      loadTeamsForSeason(leagueReady.value, seasonToSet.value).catch((error) => {
        console.error('Error loading teams:', error);
      });

      try {
        const loadedStages = await loadStagesForSeason(leagueReady.value, seasonToSet.value);
        if (cancelled) return;
        if (saved?.stage?.value) {
          const stageToSet = loadedStages.find((item) => item.value === saved.stage.value);
          if (stageToSet) setSelectedStage(stageToSet.value);
        }
      } catch (error) {
        console.error('Error loading stages:', error);
      }
    };

    restore();
    return () => {
      cancelled = true;
    };
  }, [isOpen, contextLocked, countries]);

  useEffect(() => {
    // Проверяем дубликаты когда выбраны обе команды, лига и сезон
    if (homeTeam && guestTeam && selectedLeague && selectedSeason) {
      checkForDuplicateMatch();
    } else {
      setDuplicateMatches([]);
    }
  }, [homeTeam, guestTeam, selectedLeague, selectedSeason]);

  const getDuplicateMatchTour = (match) => {
    return match?.tour_name || match?.tour || match?.round_name || match?.round || match?.tour_number || match?.short_stage_name || null;
  };

  const checkForDuplicateMatch = async () => {
    try {
      setCheckingDuplicate(true);
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();
      
      // Проверяем оба варианта: home-guest и guest-home
      const response = await axios.post(
        BASE_URL.url + '/matches/checkMatchExists',
        {
          ...champFields(champId),
          league_id: selectedLeague.value,
          season_id: selectedSeason.value,
          home_team_id: homeTeam,
          guest_team_id: guestTeam
        },
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );
      
      // Поддержка API с одним матчем и API со списком матчей
      if (response.data && response.data.exists) {
        const duplicatesFromApi = Array.isArray(response.data.matches)
          ? response.data.matches
          : response.data.match
            ? [response.data.match]
            : [];
        setDuplicateMatches(duplicatesFromApi);
      } else {
        setDuplicateMatches([]);
      }
    } catch (error) {
      // Если API не существует, делаем ручную проверку
      if (error.response && error.response.status === 404) {
        await manualCheckDuplicateMatch();
      } else {
        console.error('Error checking duplicate match:', error);
        setDuplicateMatches([]);
      }
    } finally {
      setCheckingDuplicate(false);
    }
  };

  const manualCheckDuplicateMatch = async () => {
    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();
      
      // Загружаем матчи лиги для проверки
      const response = await axios.post(
        BASE_URL.url + API_URLS.getAdminLeagueMatches,
        {
          ...champFields(champId),
          league_id: selectedLeague.value,
          season_id: selectedSeason.value,
        },
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );
      
      const matchesData = Array.isArray(response.data) ? response.data : response.data.data;
      
      if (matchesData && matchesData.length > 0) {
        // Ищем все матчи с такими же командами (в любом порядке)
        const duplicates = matchesData.filter(match => 
          (match.home_team_id === homeTeam && match.guest_team_id === guestTeam) ||
          (match.home_team_id === guestTeam && match.guest_team_id === homeTeam)
        );
        
        setDuplicateMatches(duplicates);
      } else {
        setDuplicateMatches([]);
      }
    } catch (error) {
      console.error('Error manual checking duplicate match:', error);
      setDuplicateMatches([]);
    }
  };

  const loadCountries = async () => {
    if (!champId) {
      setCountries([]);
      setLeagues([]);
      return;
    }
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountries, champFields(champId));
      setCountries(extractList(response.data).map((item, index) => ({
        ...item,
        value: item.id,
        name: item.name,
        key: index,
      })));
    } catch (error) {
      console.error('Error loading countries:', error);
      setCountries([]);
    }
  };

  const loadLeaguesForCountry = async (countryId) => {
    if (!countryId) {
      setLeagues([]);
      return [];
    }
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountry, { id: Number(countryId) });
      const nextLeagues = extractList(extractEntity(response.data).leagues)
        .map((item, index) => {
          const id = Number(item.id || item.league_id);
          return {
            ...item,
            value: id,
            name: item.name || item.league_name || '',
            key: index,
            seasons: extractList(item.seasons),
          };
        })
        .filter((item) => item.value > 0);
      setLeagues(nextLeagues);
      return nextLeagues;
    } catch (error) {
      console.error('Error loading leagues:', error);
      setLeagues([]);
      return [];
    }
  };

  const handleCountryChange = async (country_id) => {
    const country = countries.find((item) => item.value === country_id);
    setSelectedCountry(country || null);
    setSelectedLeague(null);
    setSelectedSeason(null);
    setSelectedStage(null);
    setHomeTeam(null);
    setGuestTeam(null);
    setSeasons([]);
    setStages([]);
    setTeams([]);
    setDuplicateMatches([]);
    await loadLeaguesForCountry(country_id);
  };

  const loadStadiums = async () => {
    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();
      
      const response = await axios.post(
        BASE_URL.url + API_URLS.getAllAdminStadiums,
        { ...champFields(champId) },
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );
      
      const stadiumsData = Array.isArray(response.data) ? response.data : response.data.data;
      
      if (stadiumsData && stadiumsData.length > 0) {
        const formattedStadiums = stadiumsData.map((item, index) => ({
          ...item,
          value: item.id,
          name: item.name,
          key: index
        }));
        setStadiums(formattedStadiums);
      }
    } catch (error) {
      console.error('Error loading stadiums:', error);
    }
  };

  const loadPitches = async (stadiumId) => {
    setPitches([]);
    if (!stadiumId) {
      setSelectedPitch(null);
      return;
    }
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminStadium, {
        id: Number(stadiumId),
        ...champFields(champId),
      });
      const stadium = extractEntity(response.data) || response.data || {};
      const list = Array.isArray(stadium.pitches) ? stadium.pitches : [];
      setPitches(list.map((item, index) => ({
        ...item,
        value: item.id,
        name: item.name,
        key: index,
      })));
    } catch (error) {
      setPitches([]);
    }
  };

  const loadReferees = async () => {
    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();
      
      const response = await axios.post(
        BASE_URL.url + API_URLS.getAdminStaff,
        { ...champFields(champId), role: 13, with_games: false },
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );

      console.log(response.data);
      
      const refereesData = extractAdminStaff(response.data);
      
      if (refereesData && refereesData.length > 0) {
        const formattedReferees = refereesData.map((item, index) => ({
          ...item,
          value: item.id,
          name: staffDisplayName(item),
          login: item.login || item.nickname || '',
          key: index
        }));
        setReferees(formattedReferees);
      }
    } catch (error) {
      console.error('Error loading referees:', error);
    }
  };

  const applyLeagueMatchDefaults = (league, { preferCacheStadium = true } = {}) => {
    const defaults = extractLeagueMatchDefaults(league);
    if (preferCacheStadium && defaults.stadiumId) {
      setSelectedStadium(defaults.stadiumId);
      setSelectedPitch(null);
    }
    if (defaults.refereeId) setSelectedReferee(defaults.refereeId);
    if (defaults.halfTime) setHalfTime(defaults.halfTime);
    if (defaults.extraHalfTime) setExtraHalfTime(defaults.extraHalfTime);
    if (defaults.extraTimeAvailable != null) setExtraTimeAvailable(defaults.extraTimeAvailable);
    if (defaults.penaltiesAvailable != null) setPenaltiesAvailable(defaults.penaltiesAvailable);
    if (defaults.timelineEnabled != null) setTimelineEnabled(defaults.timelineEnabled);
  };

  const fetchLeagueDetails = async (leagueId, baseLeague = null) => {
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminLeague, { id: Number(leagueId) });
      const entity = mergeLeagueInfoCache(extractEntity(response.data) || {});
      return {
        ...(baseLeague || {}),
        ...entity,
        value: Number(leagueId),
        name: entity.name || entity.league_name || baseLeague?.name || '',
        seasons: extractList(entity.seasons?.length ? entity.seasons : baseLeague?.seasons),
      };
    } catch (error) {
      return baseLeague;
    }
  };

  const handleLeagueChange = async (league_id) => {
    const league = leagues.find((item) => item.value === league_id) || null;
    
    setSelectedLeague(league);
    setSelectedSeason(null);
    setSelectedStage(null);
    setHomeTeam(null);
    setGuestTeam(null);
    setStages([]);
    setTeams([]);
    setDuplicateMatches([]);
    
    if (!league || league_id <= 0) {
      setSeasons([]);
      return;
    }

    const detailed = await fetchLeagueDetails(league_id, league);
    if (detailed) {
      setSelectedLeague(detailed);
      applyLeagueMatchDefaults(detailed);
    }

    const seasonsSource = detailed?.seasons || league.seasons;
    const formattedSeasons = extractList(seasonsSource).map((item, index) => ({
      value: item.season_id || item.id,
      name: item.season_name || item.name,
      key: index,
      ...item
    }));

    setSeasons(formattedSeasons);
  };

  const loadStagesForSeason = async (league_id, season_id) => {
    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();
      
      const stagesResponse = await axios.post(
        BASE_URL.url + `/leagues/${league_id}/getLeagueStages`,
        { 
          league_id: league_id,
          season_id: season_id 
        },
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );
      
      const stagesData = Array.isArray(stagesResponse.data) ? stagesResponse.data : stagesResponse.data.data;
      
      if (stagesData && stagesData.length > 0) {
        const formattedStages = stagesData.map((item, index) => ({
          ...item,
          value: item.id,
          name: item.name,
          key: index
        }));
        setStages(formattedStages);
        return formattedStages;
      }
      return [];
    } catch (error) {
      console.error('Error loading stages:', error);
      setStages([]);
      return [];
    }
  };

  const loadTeamsForSeason = async (league_id, season_id) => {
    setTeamsLoading(true);
    try {
      const teamsResponse = await authorizedPost(dispatch, API_URLS.getAdminSeasonTeams, {
        league_id: Number(league_id),
        season_id: Number(season_id),
        ...champFields(champId),
      });
      const payload = teamsResponse.data;
      const teamsData = (Array.isArray(payload?.teams) ? payload.teams : extractList(payload)).map((item, index) => {
        const team = normalizeSeasonTeam(item);
        return {
          ...item,
          ...team,
          value: team.team_id,
          name: team.name,
          logo: getTeamImageUrl(team.image) || getTeamImageUrl(`/images/teams/${team.team_id}.png`) || defaultTeamLogo,
          key: index,
        };
      }).filter((item) => item.value);
      setTeams(teamsData);
      return teamsData;
    } catch (error) {
      console.error('Error loading teams:', error);
      setTeams([]);
      return [];
    } finally {
      setTeamsLoading(false);
    }
  };

  const handleSeasonChange = async (season_id) => {
    const season = seasons.find((item) => item.value === season_id) || null;
    setSelectedSeason(season);
    setSelectedStage(null);
    setStages([]);
    setTeams([]);
    setHomeTeam(null);
    setGuestTeam(null);
    setDuplicateMatches([]);
    
    if (!season || !season_id || !selectedLeague) return;
    
    // Загружаем команды и стадии
    await loadTeamsForSeason(selectedLeague.value, season_id);
    await loadStagesForSeason(selectedLeague.value, season_id);
  };

  const fuzzySearch = (options) => {
    const fuse = new Fuse(options, {
      keys: ['name', 'login'],
      threshold: 0.3,
    });

    return (value) => {
      if (!value.length) {
        return options;
      }
      return fuse.search(value).map(item => item.item);
    };
  };

  const validateForm = () => {
    if (!selectedCountry) {
      setMessage({ text: t('cmNeedCountry'), type: 'error' });
      return false;
    }
    if (!selectedLeague) {
      setMessage({ text: t('leaguePlaceholder'), type: 'error' });
      return false;
    }
    if (!selectedSeason) {
      setMessage({ text: t('stSeasonPlaceholder'), type: 'error' });
      return false;
    }
    if (!selectedStage) {
      setMessage({ text: t('cmNeedStage'), type: 'error' });
      return false;
    }
    if (!homeTeam) {
      setMessage({ text: t('cmNeedHome'), type: 'error' });
      return false;
    }
    if (!guestTeam) {
      setMessage({ text: t('cmNeedGuest'), type: 'error' });
      return false;
    }
    if (homeTeam === guestTeam) {
      setMessage({ text: t('cmTeamsDifferent'), type: 'error' });
      return false;
    }
    // Без даты сервер обнуляет time при конвертации kickoff→UTC.
    if (matchTime && !matchDate) {
      setMessage({ text: t('gmTimeNeedsDate'), type: 'error' });
      return false;
    }
    return true;
  };

  const selectedStageData = stages.find((item) => item.value === selectedStage || item.id === selectedStage)
    || (preselectedStage && (preselectedStage.value === selectedStage || preselectedStage.id === selectedStage)
      ? preselectedStage
      : null);
  const stageIsPlayoff = isPlayoffStage(selectedStageData) || isPlayoffStage(preselectedStage);
  const playoffSlotCount = estimatePlayoffExpectedSlots(
    selectedStageData?.name || selectedStageData?.short_name || preselectedStage?.name,
    selectedStageData || preselectedStage
  );
  const playoffStageTitle = selectedStageData?.name
    || selectedStageData?.short_name
    || preselectedStage?.name
    || '';
  const playoffPairingHint = stageIsPlayoff
    ? playoffNetPositionPairingHint(playoffStageTitle, playoffSlotCount, selectedStageData || preselectedStage, lang)
    : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();

      const isPlayoff = stageIsPlayoff ? 1 : 0;
      const netPos = Number(netPosition);

      const stadiumObj = stadiums.find((item) => (
        Number(item.value) === Number(selectedStadium)
        || Number(item.id) === Number(selectedStadium)
      )) || null;
      const matchTimezone = resolveMatchTimezone(
        stadiumObj,
        selectedLeague,
        selectedCountry,
        championship,
      );
      const kickoff = kickoffFieldsForApi(matchDate, matchTime, matchTimezone);
      const matchData = {
        ...champFields(champId),
        home_team_id: homeTeam,
        guest_team_id: guestTeam,
        league_id: selectedLeague.value,
        stadium_id: Number(selectedStadium) || 14,
        pitch_id: selectedPitch || null,
        date: kickoff.date,
        time: kickoff.time,
        timezone: matchTimezone,
        judje_id: selectedReferee || null,
        season_id: selectedSeason.value,
        stage_id: selectedStage,
        isPlayoff,
        half_time: parseInt(halfTime),
        extra_time_available: extraTimeAvailable ? 1 : null,
        penalty_kicks_available: penaltiesAvailable ? 1 : null,
        timeline_disabled: timelineEnabled ? null : 1,
        extra_half_time: extraTimeAvailable && extraHalfTime ? parseInt(extraHalfTime) : null,
        ...(isPlayoff && Number.isFinite(netPos) && netPos >= 1
          ? { net_position: Math.round(netPos) }
          : {}),
      };

      const response = await axios.post(
        BASE_URL.url + API_URLS.setMatch,
        matchData,
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data) {
        // Сохраняем выбранные значения в localStorage
        const matchDataToSave = {
          country: selectedCountry ? {
            value: selectedCountry.value,
            name: selectedCountry.name
          } : null,
          league: {
            value: selectedLeague.value,
            name: selectedLeague.name
          },
          season: {
            value: selectedSeason.value,
            name: selectedSeason.name
          },
          stage: {
            value: selectedStage,
            name: stages.find(s => s.value === selectedStage)?.name || ''
          },
          stadium: selectedStadium,
          pitch: selectedPitch,
          referee: selectedReferee,
          date: matchDate,
          halfTime: halfTime
        };
        localStorage.setItem(LAST_MATCH_KEY, JSON.stringify(matchDataToSave));
        if (onSuccess) onSuccess(matchDate || '');
        resetForm();
        onClose();
      }
    } catch (error) {
      console.error('Error creating match:', error);
      setMessage({ 
        text: error.response?.data?.message || t('cmCreateError'), 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    if (!contextLocked) {
      setSelectedCountry(null);
      setSelectedLeague(null);
      setSelectedSeason(null);
      setSelectedStage(null);
      setSeasons([]);
      setStages([]);
      setLeagues([]);
      setTeams([]);
    }
    setNetPosition('');
    setHomeTeam(null);
    setGuestTeam(null);
    setSelectedStadium(null);
    setSelectedPitch(null);
    setSelectedReferee(null);
    setMatchDate('');
    setMatchTime('');
    setHalfTime('30');
    setExtraHalfTime('15');
    setExtraTimeAvailable(false);
    setPenaltiesAvailable(false);
    setTimelineEnabled(true);
    setDuplicateMatches([]);
    setIsHomeTeamModalOpen(false);
    setIsGuestTeamModalOpen(false);
    setMessage({ text: '', type: '' });
    if (contextLocked && selectedLeague) {
      applyLeagueMatchDefaults(selectedLeague, { preferCacheStadium: true });
    }
  };

  const clearStadium = () => {
    setSelectedStadium(null);
    setSelectedPitch(null);
    patchLastCreatedMatch({ stadium: null, pitch: null });
  };

  const clearPitch = () => {
    setSelectedPitch(null);
    patchLastCreatedMatch({ pitch: null });
  };

  const clearReferee = () => {
    setSelectedReferee(null);
    patchLastCreatedMatch({ referee: null });
  };

  const renderClearable = (hasValue, onClear, children, variant = '') => (
    <div className={`cm-clearable${hasValue ? ' has-value' : ''}${variant ? ` ${variant}` : ''}`}>
      {children}
      {hasValue ? (
        <button
          type="button"
          className="cm-picker-clear"
          aria-label={t('cmClear')}
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onClear();
          }}
        >
          ×
        </button>
      ) : null}
    </div>
  );

  const renderTeamPicker = (isHome) => {
    const open = isHome ? isHomeTeamModalOpen : isGuestTeamModalOpen;
    if (!open) return null;
    const options = isHome ? homeTeamOptions : guestTeamOptions;
    const selected = isHome ? homeTeam : guestTeam;
    const close = () => (isHome ? setIsHomeTeamModalOpen(false) : setIsGuestTeamModalOpen(false));
    const pick = (value) => {
      if (isHome) setHomeTeam(value);
      else setGuestTeam(value);
      close();
    };
    return ReactDOM.createPortal(
      <div className="team-modal-overlay" onClick={close}>
        <div className="team-modal" onClick={(event) => event.stopPropagation()}>
          <div className="team-modal-header">
            <h3>{isHome ? t('cmNeedHome') : t('cmNeedGuest')}</h3>
            <button type="button" className="team-modal-close" onClick={close}>×</button>
          </div>
          <div className="team-modal-body">
            {options.map((team) => (
              <div
                key={team.value}
                className={`cmm-team-card ${selected === team.value ? 'selected' : ''}`}
                onClick={() => pick(team.value)}
              >
                <img
                  src={team.logo || defaultTeamLogo}
                  alt={team.name}
                  className="cmm-team-card-logo"
                  onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
                />
                <span className="cmm-team-card-name">{team.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>,
      document.body
    );
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content create-match-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('cmTitle')}</h2>
          <button className="modal-close" onClick={onClose}>
            <FiX size={24} />
          </button>
        </div>
      
        <div className="modal-body">
          {message.text && (
            <div className={`message-box ${message.type === 'success' ? 'message-success' : 'message-error'}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-grid-modal">
              {/* Левая колонка */}
              <div className="form-column-modal">
                <div className="form-section-modal">
                  <label className="form-label-modal form-label-required">
                    {t('gmCountry')}
                  </label>
                  {renderClearable(Boolean(selectedCountry), () => handleCountryChange(null), (
                    <SelectSearch
                      options={countries}
                      search
                      value={selectedCountry?.value}
                      onChange={handleCountryChange}
                      filterOptions={fuzzySearch}
                      emptyMessage={t('cmCountryNotFound')}
                      placeholder={t('cmCountryPh')}
                    />
                  ))}
                </div>

                {(selectedCountry || selectedLeague || contextLocked) ? (
                  <div className="form-section-modal">
                    <label className="form-label-modal form-label-required">
                      {t('league')}
                    </label>
                    {renderClearable(Boolean(selectedLeague), () => handleLeagueChange(null), (
                      <SelectSearch
                        options={leagues}
                        search
                        value={selectedLeague?.value}
                        onChange={handleLeagueChange}
                        filterOptions={fuzzySearch}
                        emptyMessage={t('leagueNotFound')}
                        placeholder={t('leaguePlaceholder')}
                      />
                    ))}
                  </div>
                ) : null}

                {(selectedLeague || contextLocked) && (seasons.length > 0 || selectedSeason) && (
                  <div className="form-section-modal">
                    <label className="form-label-modal form-label-required">
                      {t('stSeason')}
                    </label>
                    {renderClearable(Boolean(selectedSeason), () => handleSeasonChange(null), (
                      <SelectSearch
                        options={seasons}
                        search
                        value={selectedSeason?.value}
                        onChange={handleSeasonChange}
                        filterOptions={fuzzySearch}
                        emptyMessage={t('cmSeasonNotFound')}
                        placeholder={t('stSeasonPlaceholder')}
                      />
                    ))}
                  </div>
                )}

                {(selectedSeason || selectedStage || contextLocked) && (
                  <div className="form-section-modal">
                    <label className="form-label-modal form-label-required">
                      {t('gmStage')}
                    </label>
                    {renderClearable(Boolean(selectedStage), () => {
                      setSelectedStage(null);
                      setNetPosition('');
                    }, (
                      <SelectSearch
                        options={stages}
                        search
                        value={selectedStage}
                        onChange={(stageId) => {
                          setSelectedStage(stageId);
                          setNetPosition('');
                        }}
                        filterOptions={fuzzySearch}
                        emptyMessage={stages.length === 0 ? t('cmNoStages') : t('cmStageNotFound')}
                        placeholder={t('cmNeedStage')}
                      />
                    ))}
                  </div>
                )}

                {selectedSeason && selectedStage && stageIsPlayoff ? (
                  <div className="form-section-modal">
                    <label className="form-label-modal">
                      {t('cmNetPosition')}
                    </label>
                    <select
                      className="input-field-modal"
                      value={netPosition}
                      onChange={(e) => setNetPosition(e.target.value)}
                    >
                      <option value="">{t('cmNetPositionAuto')}</option>
                      {Array.from({ length: playoffSlotCount }, (_, index) => index + 1).map((slot) => (
                        <option key={`net-pos-${slot}`} value={slot}>
                          {formatPlayoffNetPositionLabel(
                            playoffStageTitle,
                            slot,
                            selectedStageData || preselectedStage,
                            lang
                          )}
                        </option>
                      ))}
                    </select>
                    <p className="form-hint-modal" style={{ margin: '6px 0 0' }}>
                      {playoffPairingHint || t('cmNetPositionHint')}
                    </p>
                  </div>
                ) : null}

                {selectedSeason && (
                  <>
                    <div className="form-section-modal">
                      <label className="form-label-modal form-label-required">
                        {t('cmHomeTeam')}
                      </label>
                      <div
                        className="team-selector-modal"
                        onClick={() => !teamsLoading && teams.length > 0 && setIsHomeTeamModalOpen(true)}
                      >
                        {teamsLoading ? (
                          <div className="team-placeholder-modal">
                            <span>{t('cmLoadingTeams')}</span>
                          </div>
                        ) : homeTeam ? (
                          <div className="selected-team-modal">
                            <img
                              src={teams.find(t => t.value === homeTeam)?.logo || defaultTeamLogo}
                              alt=""
                              className="team-logo-modal"
                              onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
                            />
                            <span className="team-name-modal">{teams.find(t => t.value === homeTeam)?.name}</span>
                            <button
                              type="button"
                              className="cm-picker-clear is-inline"
                              aria-label={t('cmClear')}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                setHomeTeam(null);
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <div className="team-placeholder-modal">
                            <span>{teams.length ? t('cmPickTeam') : t('cmNoTeams')}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="form-section-modal">
                      <label className="form-label-modal form-label-required">
                        {t('cmGuestTeam')}
                      </label>
                      <div
                        className="team-selector-modal"
                        onClick={() => !teamsLoading && teams.length > 0 && setIsGuestTeamModalOpen(true)}
                      >
                        {teamsLoading ? (
                          <div className="team-placeholder-modal">
                            <span>{t('cmLoadingTeams')}</span>
                          </div>
                        ) : guestTeam ? (
                          <div className="selected-team-modal">
                            <img
                              src={teams.find(t => t.value === guestTeam)?.logo || defaultTeamLogo}
                              alt=""
                              className="team-logo-modal"
                              onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
                            />
                            <span className="team-name-modal">{teams.find(t => t.value === guestTeam)?.name}</span>
                            <button
                              type="button"
                              className="cm-picker-clear is-inline"
                              aria-label={t('cmClear')}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                setGuestTeam(null);
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <div className="team-placeholder-modal">
                            <span>{teams.length ? t('cmPickTeam') : t('cmNoTeams')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Предупреждение о дубликате */}
                {checkingDuplicate && (
                  <div className="duplicate-check-loading">
                    {t('cmCheckingDuplicates')}
                  </div>
                )}
                
                {duplicateMatches.length > 0 && !checkingDuplicate && (
                  <div className="duplicate-warning">
                    <div className="duplicate-warning-header">
                      ⚠️ {t('cmDuplicatesFound', { count: duplicateMatches.length })}
                    </div>
                    {duplicateMatches.map((match, index) => (
                      <div
                        key={match.id || `${match.home_team_id}-${match.guest_team_id}-${match.date || index}`}
                        className="duplicate-match-info"
                      >
                        <div className="duplicate-match-teams">
                          <span>{match.home_team?.name || match.home_team_name || teams.find(t => t.value === match.home_team_id)?.name}</span>
                          <span> vs </span>
                          <span>{match.guest_team?.name || match.guest_team_name || teams.find(t => t.value === match.guest_team_id)?.name}</span>
                        </div>
                        {getDuplicateMatchTour(match) && (
                          <div className="duplicate-match-tour">
                            {t('cmTour')}: {getDuplicateMatchTour(match)}
                          </div>
                        )}
                        {match.date && (
                          <div className="duplicate-match-date">
                            {t('gmDate')}: {formatKickoff(match)}
                          </div>
                        )}
                        {match.stadium?.name || match.stadium_name ? (
                          <div className="duplicate-match-stadium">
                            {t('stadium')}: {match.stadium?.name || match.stadium_name}
                          </div>
                        ) : null}
                      </div>
                    ))}
                    <div className="duplicate-warning-note">
                      {t('cmStillCreate')}
                    </div>
                  </div>
                )}
              </div>

              {/* Правая колонка */}
              <div className="form-column-modal">
                <div className="form-section-modal">
                  <label className="form-label-modal">
                    {t('stadium')}
                  </label>
                  {renderClearable(Boolean(selectedStadium), clearStadium, (
                    <SelectSearch
                      options={stadiums}
                      search
                      value={selectedStadium}
                      onChange={(value) => {
                        setSelectedStadium(value);
                        setSelectedPitch(null);
                      }}
                      filterOptions={fuzzySearch}
                      emptyMessage={t('cmStadiumNotFound')}
                      placeholder={t('cmStadiumPh')}
                    />
                  ))}
                </div>

                {selectedStadium && pitches.length > 0 && (
                  <div className="form-section-modal">
                    <label className="form-label-modal">
                      {t('gmPitch')}
                    </label>
                    {renderClearable(Boolean(selectedPitch), clearPitch, (
                      <SelectSearch
                        options={pitches}
                        search
                        value={selectedPitch}
                        onChange={setSelectedPitch}
                        filterOptions={fuzzySearch}
                        emptyMessage={t('gmPitchNotFound')}
                        placeholder={t('gmPitchPlaceholder')}
                      />
                    ))}
                  </div>
                )}

                <div className="form-section-modal">
                  <label className="form-label-modal">
                    {t('referee')}
                  </label>
                  {renderClearable(Boolean(selectedReferee), clearReferee, (
                    <SelectSearch
                      options={referees}
                      search
                      value={selectedReferee}
                      onChange={setSelectedReferee}
                      filterOptions={fuzzySearch}
                      emptyMessage={t('cmRefereeNotFound')}
                      placeholder={t('cmRefereePh')}
                      renderOption={(domProps, option, snapshot, className) => (
                        <button type="button" {...domProps} className={className}>
                          <span className="gm-referee-option">
                            <span>{option.name}</span>
                            {option.login ? <span className="gm-referee-login">{option.login}</span> : null}
                          </span>
                        </button>
                      )}
                    />
                  ))}
                </div>

                <div className="date-time-row-modal">
                  <div className="form-section-modal">
                    <label className="form-label-modal">
                      {t('cmDateOptional')}
                    </label>
                    {renderClearable(Boolean(matchDate), () => {
                      setMatchDate('');
                      patchLastCreatedMatch({ date: '' });
                    }, (
                      <div className="cm-native-dt">
                        <input
                          className="input-field-modal"
                          type="date"
                          value={matchDate}
                          onChange={(e) => setMatchDate(e.target.value)}
                        />
                      </div>
                    ), 'is-input')}
                  </div>

                  <div className="form-section-modal">
                    <label className="form-label-modal">
                      {t('time')}
                    </label>
                    {renderClearable(Boolean(matchTime), () => setMatchTime(''), (
                      <div className="cm-native-dt">
                        <input
                          className="input-field-modal"
                          type="time"
                          value={matchTime}
                          onChange={(e) => setMatchTime(e.target.value)}
                        />
                      </div>
                    ), 'is-input')}
                  </div>
                </div>

                <div className="form-section-modal">
                  <label className="form-label-modal">
                    {t('cmHalfMinutes')}
                  </label>
                  <input
                    className="input-field-modal"
                    type="number"
                    value={halfTime}
                    onChange={(e) => setHalfTime(e.target.value)}
                    min="1"
                  />
                </div>

                <div className="form-section-modal">
                  <label className="checkbox-item-modal">
                    <input
                      type="checkbox"
                      checked={extraTimeAvailable}
                      onChange={(e) => setExtraTimeAvailable(e.target.checked)}
                    />
                    <span>{t('gmExtraTime')}</span>
                  </label>
                </div>

                {extraTimeAvailable && (
                  <div className="form-section-modal" style={{ marginLeft: '24px' }}>
                    <label className="form-label-modal">
                      {t('gmExtraHalfTime')}
                    </label>
                    <input
                      className="input-field-modal"
                      type="number"
                      value={extraHalfTime}
                      onChange={(e) => setExtraHalfTime(e.target.value)}
                      min="1"
                    />
                  </div>
                )}

                <div className="form-section-modal">
                  <label className="checkbox-item-modal">
                    <input
                      type="checkbox"
                      checked={penaltiesAvailable}
                      onChange={(e) => setPenaltiesAvailable(e.target.checked)}
                    />
                    <span>{t('gmPenaltiesAvailable')}</span>
                  </label>
                </div>

                <div className="form-section-modal">
                  <label className="checkbox-item-modal">
                    <input
                      type="checkbox"
                      checked={timelineEnabled}
                      onChange={(e) => setTimelineEnabled(e.target.checked)}
                    />
                    <span>
                      {t('gmEnableTimeline')}
                      <span className="gm-flag-hint">{t('gmTimelineHint')}</span>
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="button-group-modal">
              <button
                type="submit"
                disabled={loading}
                className="btn-modal btn-primary-modal"
              >
                {loading ? t('cmCreating') : t('createMatch')}
              </button>

              <button
                type="button"
                onClick={resetForm}
                disabled={loading}
                className="btn-modal btn-secondary-modal"
              >
                {t('cmClear')}
              </button>

              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="btn-modal btn-secondary-modal"
              >
                {t('cancel')}
              </button>
            </div>
          </form>

          {renderTeamPicker(true)}
          {renderTeamPicker(false)}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default CreateMatchModal;
